import {
  hasResolvedDocumentValue,
  useDocument,
} from "@shared/components/providers/SupergrainProvider"
import { calculateCourseDateCombined } from "@shared/lib/course-date-combined"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined as CourseDateCombinedModel } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import { type ReactNode } from "react"
import { ThreeDotSpinner } from "react-app/components/util/ThreeDotSpinner"
import { ensurePhantomLesson, usePhantomLessonDeps } from "react-app/lib/phantom-lesson"

/** Ember's block param, plus the documents it derived from — nothing below reads them again. */
type Yield = (
  courseDate: CourseDateCombinedModel,
  course: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
) => ReactNode

interface Props {
  courseId: string
  planbookId: string
  rotationCalendarId: string
  dateString: string
  children: Yield
}

/** `Planner::Course::Date::CourseDateCombined` for React. */
export default tracked(function CourseDateCombined({
  courseId,
  planbookId,
  rotationCalendarId,
  dateString,
  children,
}: Props) {
  const deps = usePhantomLessonDeps()
  const courseHandle = useDocument("course", courseId)
  const planbookHandle = useDocument("planbook", planbookId)
  const rotationCalendarHandle = useDocument("rotation-calendar", rotationCalendarId)

  if (
    courseHandle.status === "error" ||
    planbookHandle.status === "error" ||
    rotationCalendarHandle.status === "error"
  ) {
    throw courseHandle.error ?? planbookHandle.error ?? rotationCalendarHandle.error
  }

  if (
    !hasResolvedDocumentValue(courseHandle) ||
    !hasResolvedDocumentValue(planbookHandle) ||
    !hasResolvedDocumentValue(rotationCalendarHandle)
  ) {
    return <ThreeDotSpinner />
  }

  const course = courseHandle.value
  const planbook = planbookHandle.value
  const rotationCalendar = rotationCalendarHandle.value

  const courseDate = calculateCourseDateCombined(
    dateString,
    course,
    planbook,
    rotationCalendar,
    // This world's `findCardStackIdForDate`: mints on first ask, same timing as the Ember service.
    (ownerId, date) =>
      ensurePhantomLesson(deps, {
        courseId: ownerId,
        planbookId: planbook.id,
        dateString: date,
        permissions: course.meta.permissions ?? [],
      }),
  )

  return children(courseDate, course, planbook, rotationCalendar)
})
